import { Theme, css } from '@emotion/react';

const height = 600;
const desktopWidth = 1100;
const mobileWidth = 375;
const colorBlack = '#2B2B2B';

export const root = (theme: Theme) => css`
  --scale: 1;
  display: flex;
  height: calc(${height}px * var(--scale));
  margin: 2rem 0;
  width: calc(${desktopWidth + mobileWidth + theme.spacing(2)}px * var(--scale));
`;

export const desktopRoot = (theme: Theme) => css`
  height: calc(${height}px * var(--scale));
  margin-right: ${theme.spacing(2)}px;
  overflow: hidden;
  width: calc(${desktopWidth}px * var(--scale));
`;

export const desktopItems = (theme: Theme) => css`
  display: grid;
  gap: ${theme.spacing(1)}px;
  grid-auto-columns: 1fr;
  grid-auto-rows: 1fr;
  transform: scale(var(--scale))
    translate(
      calc((1 - var(--scale)) / var(--scale) * -50%),
      calc((1 - var(--scale)) / var(--scale) * -50%)
    );
  width: ${desktopWidth}px;
`;

export const desktopItem = (theme: Theme) => css`
  background-color: ${colorBlack};
  background-position: center;
  background-size: cover;
  height: auto;

  & > a {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: flex-end;
    overflow-y: hidden;
    text-decoration: none;

    & .MarketingFeature-backdrop {
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      display: block;
      flex: 1;
      width: 100%;
    }

    & .MarketingFeature-meta {
      background: ${colorBlack};
      box-shadow: 0 ${theme.spacing(1)}px 0 0 ${colorBlack};
      padding: ${theme.spacing(3)}px;
      padding-right: ${theme.spacing(4)}px;
      transform: translateY(0) translateZ(0);
    }

    &:hover {
      text-decoration: none;

      & .MarketingFeature-meta {
        background: ${colorBlack};
        box-shadow: 0 ${theme.spacing(1)}px 0 0 ${colorBlack};
        transform: translateY(${theme.spacing(-1)}px) translateZ(0);
      }

      &.Mui-focusVisible .MarketingFeature-meta {
        box-shadow: 0 ${theme.spacing(1)}px 0 0 ${theme.colors.primary};
      }
    }

    &:focus {
      box-shadow: none;
    }

    &.Mui-focusVisible {
      &,
      &:focus {
        box-shadow: 0 0 0 2px ${theme.colors.borderPrimary};

        & .MarketingFeature-meta {
          background: ${theme.colors.backgroundPrimary};
        }
      }
    }
  }

  &:nth-of-type(1) {
    grid-column: 1 / span 2;
    grid-row: 1 / span 2;
    height: ${height}px;
  }

  &:nth-of-type(2) {
    &.featureDesktopOfThree {
      grid-column: 3 / span 2;
      grid-row: 1 / 2;
    }
    &.featureDesktopOfFour,
    &.featureDesktopOfFive {
      grid-column: 3 / 3;
      grid-row: 1 / 1;
    }
  }

  &:nth-of-type(3) {
    &.featureDesktopOfThree {
      grid-column: 3 / span 2;
      grid-row: 2 / 2;
    }
    &.featureDesktopOfFour,
    &.featureDesktopOfFive {
      grid-column: 4 / 4;
      grid-row: 1 / 1;
    }
  }

  &:nth-of-type(4) {
    &.featureDesktopOfFour {
      grid-column: 3 / span 2;
      grid-row: 2 / 2;
    }
    &.featureDesktopOfFive {
      grid-column: 3 / 4;
      grid-row: 2 / 2;
    }
  }

  &:nth-of-type(5) {
    grid-column: 4 / 4;
    grid-row: 2 / 2;
  }
`;

export const desktopTitle = (theme: Theme) => css`
  color: ${theme.colors.textWhite};
  font-family: 'Centra No2';
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
`;

export const desktopDescription = (theme: Theme) => css`
  color: ${theme.colors.textWhite};
  font-family: 'Centra No2';
  font-size: 12px;
  font-weight: 500;
  line-height: ${16 / 12};
  margin: 0;

  ${theme.breakpoints.up('lg')} {
    font-size: 14px;
    line-height: ${16 / 14};
  }
`;

export const mobileRoot = (theme: Theme) => css`
  background: ${colorBlack};
  display: block;
  height: calc(${height}px * var(--scale));
  width: calc(${mobileWidth}px * var(--scale));
`;

export const mobileItems = (theme: Theme) => css`
  display: flex;
  flex-wrap: wrap;
  height: ${height}px;
  justify-content: space-around;
  overflow: hidden;
  transform: scale(var(--scale))
    translate(
      calc((1 - var(--scale)) / var(--scale) * -50%),
      calc((1 - var(--scale)) / var(--scale) * -50%)
    );
  width: ${mobileWidth}px;

  & .scroll-area {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-padding: ${theme.spacing(3)}px;
    padding-top: ${theme.spacing(9)}px;
    padding-bottom: ${theme.spacing(9)}px;
    transform: translateZ(0);

    & > *:first-of-type {
      padding-left: calc((${mobileWidth}px - 26rem) / 2);
    }

    & > *:last-child {
      padding-right: calc((${mobileWidth}px - 26rem) / 2);
    }
  }
`;

export const mobileItem = (theme: Theme) => css`
  margin-right: ${theme.spacing(3)}px;
  scroll-snap-align: start;

  & > a {
    display: block;
    padding: 2px;
    position: relative;
    text-decoration: none;
    width: 26rem;

    &:hover {
      text-decoration: none;
    }
  }

  & .MarketingFeature-backdrop-wrapper {
    height: 100%;
    left: 0;
    overflow: hidden;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 1;

    & .MarketingFeature-backdrop {
      background-position: center;
      background-size: cover;
      display: block;
      filter: blur(12px) brightness(1.1);
      height: 100%;
      transform: scale(1.1);
      width: 100%;
    }
  }

  & .MarketingFeature-content-wrapper {
    background: ${colorBlack};
    padding: ${theme.spacing(1)}px;
    position: relative;
    z-index: 2;

    & .MarketingFeature-image {
      background-position: center;
      background-size: cover;
      display: block;
      width: 100%;

      &::after {
        content: '';
        display: block;
        padding-bottom: 100%;
      }
    }

    & .MarketingFeature-meta {
      padding: ${theme.spacing(2.5)}px ${theme.spacing(1)}px ${theme.spacing(2.5)}px
        ${theme.spacing(1.5)}px;
    }
  }
`;

export const mobileTitle = (theme: Theme) => css`
  color: ${theme.colors.textWhite};
  font-size: 36px;
  font-family: 'Söhne Schmal', Helvetica, sans-serif;
  font-weight: 700;
  line-height: 1;
  letter-spacing: normal;
  margin-bottom: ${theme.spacing(1)}px;
  text-transform: none;
`;

export const mobileDescription = (theme: Theme) => css`
  color: ${theme.colors.textWhite};
  font-size: 12px;
  font-family: 'Centra No2', Helvetica, sans-serif;
  font-weight: 500;
  line-height: 1.3333333;
  letter-spacing: normal;
  text-transform: none;
`;
